import {Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {DEFAULT_ZOOM} from "../../core/constants/constants";

@Directive({
  selector: '[appDynamicWidth]',
  standalone: true
})
export class DynamicWidthDirective implements OnInit, OnDestroy {
  @Input({ required: true }) currentTableZoom: number = DEFAULT_ZOOM;
  @Input() textInput: boolean = false;
  @Input({ required: true }) parsedMinWidth: number = 0;
  @Input() parsedMaxWidth: number = 0;

  private readonly sizeFactor: number = 0.9;
  private zoomFactor: number = 1;
  private isFocused: boolean = false;
  private canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D | null;
  private scrollContainer: HTMLElement | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
  }

  public ngOnInit(): void {
    this.setWidth(this.parsedMinWidth);
    this.findScrollContainer();
  }

  public ngOnDestroy(): void {
    this.removeScrollBlockClass();
  }

  @HostListener('focus') onFocus(): void {
    if (!this.isFocused) {
      this.isFocused = true;
    }

    this.zoomFactor = this.calculateZoomFactor();
    this.calculateMaxWidth();
    this.setFocusClass();
    this.addScrollBlockClass();
    this.updateWidth();
  }

  @HostListener('input') onInput(): void {
    this.updateWidth();
  }

  @HostListener('blur') onBlur(): void {
    this.removeFocusClass();
    this.removeScrollBlockClass();
    this.setWidth(this.parsedMinWidth);
    this.isFocused = false;
  }

  private findScrollContainer(): void {
    this.scrollContainer = document.querySelector('.cdk-virtual-scrollable');
  }

  private addScrollBlockClass(): void {
    if (this.scrollContainer) {
      this.renderer.addClass(this.scrollContainer, 'no-scroll');
    }
  }

  private removeScrollBlockClass(): void {
    if (this.scrollContainer) {
      this.renderer.removeClass(this.scrollContainer, 'no-scroll');
    }
  }

  private setWidth(width: number): void {
    if (width > this.parsedMaxWidth) { return; }

    this.renderer.setStyle(this.el.nativeElement, 'width', `${width}px`);
  }

  private setFocusClass(): void {
    const stickyParent = this.findStickyParent();
    this.renderer.addClass(this.el.nativeElement, 'dynamic-focus');

    if (!stickyParent) { return; }

    this.renderer.addClass(stickyParent, 'sticky_edit');
    this.renderer.addClass(this.el.nativeElement, 'dynamic-focus_sticky');
  }

  private removeFocusClass(): void {
    const stickyParent = this.findStickyParent();
    this.renderer.removeClass(this.el.nativeElement, 'dynamic-focus');

    if (!stickyParent) { return; }

    this.renderer.removeClass(stickyParent, 'sticky_edit');
    this.renderer.removeClass(this.el.nativeElement, 'dynamic-focus_sticky');
  }

  private updateWidth(): void {
    if (!this.context) { return; }

    const computedStyle = window.getComputedStyle(this.el.nativeElement);
    this.context.font = computedStyle.font;

    const text = this.el.nativeElement.value || this.el.nativeElement.textContent || '';
    const textWidth = this.context.measureText(text).width;
    const adjustedMaxWidth = this.parsedMaxWidth / this.zoomFactor;
    const clampedWidth = Math.min(Math.max(this.parsedMinWidth, textWidth), adjustedMaxWidth);

    this.setWidth(clampedWidth);
  }

  private calculateZoomFactor(): number {
    return this.currentTableZoom / 100;
  }

  private calculateMaxWidth(): void {
    const elementRect = this.el.nativeElement.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const remainingWidth = windowWidth - elementRect.left;
    this.parsedMaxWidth = remainingWidth * this.sizeFactor;
  }

  private findStickyParent(): HTMLElement | null {
    return this.el.nativeElement.closest('.table-body-cell.sticky') || this.el.nativeElement.closest('.body-cell.sticky');
  }
}
